<template>
  <div v-if="visible" class="otp-modal-overlay" @click.self="close">
    <div class="otp-modal">
      <button class="close-btn" @click="close">
        <i class="fa fa-times"></i>
      </button>
      
      <h3 class="title">{{ title || $t('otp.verify_title') }}</h3>
      <p class="description" v-html="$t('otp.description')"></p>

      <div class="otp-inputs">
        <input
          v-for="(value, index) in otp"
          :key="index"
          ref="otpInput"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="otp-input"
          :class="{ 'has-error': !!errorMessage }"
          v-model="otp[index]"
          @input="onInput($event, index)"
          @keydown.backspace="onBackspace(index)"
          @paste="onPaste"
        />
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <div class="resend-section">
        <span v-if="countdown > 0">
          {{ $t('otp.resend_in') }} <span class="countdown">{{ formatCountdown }}</span>
        </span>
        <span v-else class="can-resend">{{ $t('otp.can_resend') }}</span>
      </div>

      <div class="actions">
        <button 
          class="btn-resend" 
          @click="handleResend"
          :disabled="countdown > 0"
        >
          {{ $t('otp.resend') }}
        </button>
        <button 
          class="btn-verify" 
          @click="handleVerify"
        >
          {{ $t('otp.verify') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import rf from "@/request/RequestFactory";

export default {
  name: 'VerifyOTP',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: ''
    },
    isVerified: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      otp: ['', '', '', '', '', ''],
      countdown: 180, //seconds
      countdownInterval: null,
      errorMessage: '',
    };
  },
  computed: {
    isComplete() {
      return this.otp.every(v => v !== '');
    },
    formatCountdown() {
      const minutes = Math.floor(this.countdown / 60);
      const seconds = this.countdown % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.reset();
        this.startCountdown();
        this.$nextTick(() => {
          this.focusInput(0);
        });
      } else {
        this.stopCountdown();
      }
    },
  },
  methods: {
    startCountdown() {
      this.countdown = 180;
      this.stopCountdown();
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.stopCountdown();
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    async handleResend() {
      if (this.countdown > 0) return;
      this.resendOtpCode();
    },

    resendOtpCode() {
      // Emit event to parent component to handle resending OTP
      // Parent has the necessary context (platform, token, email)
      this.$emit('resend');
      this.startCountdown();
      this.reset();
    },

    handleVerify() {
      if (!this.isComplete) {
        this.errorMessage = this.$t('otp.error_full_code');
        return;
      }
      this.errorMessage = '';
      this.$emit('complete', this.otp.join(''));
    },

    setError(message) {
      this.errorMessage = message || this.$t('otp.error_invalid_code');
    },

    focusInput(index) {
      const inputs = this.$refs.otpInput;
      if (inputs && inputs[index]) {
        inputs[index].focus();
      }
    },

    onInput(event, index) {
      const value = event.target.value.replace(/\D/g, '');
      this.$set(this.otp, index, value);
      this.errorMessage = '';

      if (value && index < 5) {
        this.focusInput(index + 1);
      }
    },

    onBackspace(index) {
      if (!this.otp[index] && index > 0) {
        this.focusInput(index - 1);
      }
    },

    onPaste(event) {
      event.preventDefault();
      const paste = event.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 6);

      paste.split('').forEach((char, index) => {
        this.$set(this.otp, index, char);
      });
      this.errorMessage = '';

      const nextIndex = paste.length < 6 ? paste.length : 5;
      this.$nextTick(() => {
        this.focusInput(nextIndex);
      });
    },

    close() {
      this.$emit('close');
    },

    reset() {
      this.otp = ['', '', '', '', '', ''];
      this.errorMessage = '';
    },
  },
  beforeDestroy() {
    this.stopCountdown();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass/variables";

.otp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.otp-modal {
  background: #2c3038;
  padding: 40px 35px;
  border-radius: 12px;
  width: 420px;
  max-width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  position: relative;
  
  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: transparent;
    border: none;
    color: #8b92a7;
    font-size: 18px;
    cursor: pointer;
    padding: 5px;
    transition: color 0.2s;
    
    &:hover {
      color: #fff;
    }
  }
}

.title {
  text-align: center;
  margin-bottom: 12px;
  font-family: $font-inter-bold;
  color: #ffffff;
  font-size: 22px;
  line-height: 1.3;
}

.description {
  display: block;
  text-align: center;
  margin-bottom: 30px;
  font-family: $font-inter-regular;
  color: #8b92a7;
  font-size: 13px;
  line-height: 1.5;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
}

.otp-input {
  width: 50px;
  height: 60px;
  font-size: 24px;
  text-align: center;
  background: #1e2128;
  border: 2px solid transparent;
  border-radius: 8px;
  font-family: $font-inter-medium;
  color: #ffffff;
  transition: all 0.2s;
  
  &:focus {
    border-color: #00d09c;
    outline: none;
    background: #252930;
  }

  &:not(:placeholder-shown) {
    border-color: #3a3f4a;
  }

  &.has-error {
    border-color: rgba(255, 74, 74, 0.5);
  }
}

.error-message {
  color: #ff4a4a;
  font-size: 13px;
  text-align: center;
  margin-top: -10px;
  margin-bottom: 20px;
  font-family: $font-inter-regular;
}

.resend-section {
  text-align: center;
  margin-bottom: 25px;
  font-family: $font-inter-regular;
  font-size: 14px;
  color: #8b92a7;
  min-height: 20px;

  .countdown {
    color: #00d09c;
    font-family: $font-inter-medium;
  }

  .can-resend {
    color: #00d09c;
  }
}

.actions {
  display: flex;
  gap: 12px;
  
  button {
    flex: 1;
    padding: 14px 0;
    font-size: 15px;
    border-radius: 8px;
    font-family: $font-inter-medium;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .btn-resend {
    background: #3a3f4a;
    color: #ffffff;
    
    &:hover:not(:disabled) {
      background: #454b58;
    }
  }

  .btn-verify {
    background: #00d09c;
    color: #ffffff;
    
    &:hover:not(:disabled) {
      background: #00ba89;
    }
  }
}
</style>
